import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { FiFilter, FiSearch, FiChevronDown } from 'react-icons/fi'
import CourseCard from './CourseCard'

interface CoursesData {
  allMdx: {
    nodes: Array<{
      id: string
      frontmatter: {
        title: string
        division: string
        requirements?: string
      }
      fields: {
        slug: string
      }
    }>
  }
}

const CoursesList: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDivisions, setSelectedDivisions] = useState<Set<string>>(new Set())
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const data = useStaticQuery<CoursesData>(graphql`
    query CoursesListQuery {
      allMdx(
        filter: { internal: { contentFilePath: { regex: "/content/courses/" } } }
        sort: { frontmatter: { title: ASC } }
      ) {
        nodes {
          id
          frontmatter {
            title
            division
            requirements
          }
          fields {
            slug
          }
        }
      }
    }
  `)

  const courses = data.allMdx.nodes

  const allDivisions = useMemo(() => {
    const divisionSet = new Set<string>()
    courses.forEach((course) => {
      if (course.frontmatter.division) {
        divisionSet.add(course.frontmatter.division)
      }
    })
    return Array.from(divisionSet).sort()
  }, [courses])

  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const matchesSearch = searchQuery === '' ||
        course.frontmatter.title.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesDivision = selectedDivisions.size === 0 ||
        (course.frontmatter.division &&
         selectedDivisions.has(course.frontmatter.division))

      return matchesSearch && matchesDivision
    })
  }, [courses, searchQuery, selectedDivisions])

  const toggleDivision = (division: string) => {
    setSelectedDivisions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(division)) {
        newSet.delete(division)
      } else {
        newSet.add(division)
      }
      return newSet
    })
  }

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedDivisions(new Set())
    setIsDropdownOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  if (courses.length === 0) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-semibold text-text-light mb-4">
          No courses found
        </h2>
        <p className="text-text-light">
          Courses will appear here once they're added to the system.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 pl-10 bg-gray-100 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent focus:bg-white transition-colors"
          />
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
        </div>

        {allDivisions.length > 0 && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 min-w-[140px] justify-between"
            >
              <div className="flex items-center gap-2">
                <FiFilter className="w-4 h-4 text-gray-500" />
                <span className="text-gray-700 font-medium">
                  Division {selectedDivisions.size > 0 && `(${selectedDivisions.size})`}
                </span>
              </div>
              <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                <div className="p-3 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Filter by division</span>
                    {selectedDivisions.size > 0 && (
                      <button
                        onClick={() => setSelectedDivisions(new Set())}
                        className="text-xs text-primary hover:text-primary-dark"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto p-3">
                  {allDivisions.map((division) => (
                    <label
                      key={division}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedDivisions.has(division)}
                        onChange={() => toggleDivision(division)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700">{division}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {(selectedDivisions.size > 0 || searchQuery) && (
          <button
            onClick={clearFilters}
            className="px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors text-gray-700 font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {filteredCourses.length === 0 ? (
        <div className="text-center py-16">
          <h2 className="text-2xl font-semibold text-text-light mb-4">
            No courses match your filters
          </h2>
          <p className="text-text-light mb-6">
            Try adjusting your search or filter criteria.
          </p>
          <button
            onClick={clearFilters}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Clear all filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCourses.map((course) => (
            <CourseCard
              key={course.id}
              title={course.frontmatter.title}
              division={course.frontmatter.division}
              requirements={course.frontmatter.requirements}
              slug={course.fields.slug}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default CoursesList